import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';

interface Bank {
  id: string;
  name: string;
  description: string;
  foundedYear: number;
  headquarters: string;
  services: string[];
  website?: string;
}

const banks: Bank[] = [
  {
    id: 'mybank',
    name: 'MyBank LTD Somalia',
    description: 'MyBank is a modern digital bank established in 2018, offering comprehensive financial services including personal banking, business banking, and Islamic banking solutions.',
    foundedYear: 2018,
    headquarters: 'Mogadishu',
    services: ['Digital Banking', 'Islamic Banking', 'Business Banking', 'International Transfers'],
    website: 'https://mybank.so'
  },
  {
    id: 'amal-bank',
    name: 'Amal Bank',
    description: 'Amal Bank is a leading Islamic bank in Somalia, providing Shariah-compliant banking services to individuals and businesses.',
    foundedYear: 2009,
    headquarters: 'Mogadishu',
    services: ['Islamic Banking', 'Personal Banking', 'Business Banking']
  },
  {
    id: 'premier-bank',
    name: 'Premier Bank',
    description: 'Premier Bank is one of Somalia\'s established financial institutions, offering a wide range of banking services.',
    foundedYear: 2014,
    headquarters: 'Mogadishu',
    services: ['Retail Banking', 'Corporate Banking', 'Trade Finance']
  },
  {
    id: 'salaam-bank',
    name: 'Salaam Somali Bank',
    description: 'Salaam Somali Bank was the first privately-owned bank in Somalia, pioneering modern banking services since 2009.',
    foundedYear: 2009,
    headquarters: 'Mogadishu',
    services: ['Personal Banking', 'Corporate Banking', 'International Banking']
  }
];

const commonQuestions = [
  {
    question: "What is the largest bank in Somalia?",
    answer: "Several major banks operate in Somalia, including MyBank LTD, Amal Bank, and Salaam Somali Bank. Each serves different market segments with various financial products."
  },
  {
    question: "Are Somali banks internationally recognized?",
    answer: "Yes, many Somali banks have international partnerships and offer SWIFT transfers. They are regulated by the Central Bank of Somalia and follow international banking standards."
  },
  {
    question: "What types of banking services are available in Somalia?",
    answer: "Somali banks offer a wide range of services including personal banking, business banking, Islamic banking, international transfers, mobile banking, and trade finance."
  }
];

const BanksList: React.FC = () => {
  const location = useLocation();
  const origin = window.location.origin;
  const fullUrl = `${origin}${location.pathname}`;

  const seoKeywords = [
    'MyBank LTD',
    'MyBank',
    'MyBank Somalia',
    'MyBank LTD Somalia',
    'MyBank LTD Somali',
    'best bank in somalia',
    'best somali banks',
    'Somalia Banks',
    'Somali Banking',
    'Bangiyada ugu fiican Soomaaliya',
    'banking in somalia',
    'bankiga somali ugu fiican',
    'banks in Somalia',
    'Somali banks',
    'Somalia banking sector',
    'licensed banks Somalia',
    'banking services Somalia',
    'Islamic banks Somalia',
    'Mogadishu banks',
    'Somalia financial institutions'
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": commonQuestions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": banks.map((bank, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "BankOrCreditUnion",
        "name": bank.name,
        "description": bank.description,
        "foundingDate": bank.foundedYear.toString(),
        "location": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": bank.headquarters,
            "addressCountry": "Somalia"
          }
        }
      }
    }))
  };

  return (
    <>
      <SEO 
        title="List of Banks in Somalia - Complete Banking Directory"
        description="Comprehensive directory of licensed banks in Somalia. Find information about banking services, Islamic banking, and financial institutions in Somalia."
        keywords={seoKeywords}
        type="article"
        datePublished="2024-02-20"
        dateModified={new Date().toISOString().split('T')[0]}
        jsonLd={[faqSchema, itemListSchema]}
      />
      {/* Rest of your component code */}
    </>
  );
};

export default BanksList; 